/** Matches Individual Sequence news source links: [label] + secondary underline */
export const CONTENT_LINK_CLASS =
  "content-link text-[10px] text-secondary underline cursor-pointer";

export function toAbsoluteUrl(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}(\/.*)?$/.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return null;
}

export function getBracketLinkLabel(url, raw = "") {
  const rawTrimmed = String(raw).trim();
  const absoluteUrl = toAbsoluteUrl(url) || toAbsoluteUrl(rawTrimmed);
  if (absoluteUrl) {
    try {
      return new URL(absoluteUrl).hostname.replace(/^www\./, "");
    } catch {
      /* fall through */
    }
  }
  return rawTrimmed || "Link";
}

export function resolveContentLink(raw) {
  const text = String(raw ?? "").trim();
  const url = toAbsoluteUrl(text);
  if (!url) return null;
  return {
    url,
    text,
    displayText: getBracketLinkLabel(url, text),
  };
}

function shouldRewriteAnchorLabel(text, href) {
  const inner = String(text ?? "").trim();
  if (!inner) return true;
  if (inner.toLowerCase() === "view source") return true;
  if (inner === href) return true;
  if (/^https?:\/\//i.test(inner)) return true;
  if (inner.length > 80) return true;
  return false;
}

function formatAnchorLabel(text, href) {
  const inner = String(text ?? "").trim();
  if (shouldRewriteAnchorLabel(inner, href)) {
    return `[${getBracketLinkLabel(href, inner || href)}]`;
  }
  if (inner.startsWith("[") && inner.endsWith("]")) return inner;
  return inner;
}

/** Turn bare URLs and (Source: …) / (domain.tld) patterns into markdown links before parse. */
export function linkifyContentMarkdownSource(text) {
  if (!text || typeof text !== "string") return text;

  let result = text;

  result = result.replace(/\(Source:\s*([^)]+)\)/gi, (_, source) => {
    const link = resolveContentLink(source.trim());
    if (!link) return `(Source: ${source})`;
    return `[${link.displayText}](${link.url})`;
  });

  result = result.replace(
    /\(([a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}(?:\/[^)\s]*)?)\)/g,
    (match, domain) => {
      const link = resolveContentLink(domain);
      if (!link) return match;
      return `[${link.displayText}](${link.url})`;
    },
  );

  result = result.replace(/(?<!\]\()(https?:\/\/[^\s<>\])"'`]+)/gi, (url) => {
    const link = resolveContentLink(url);
    if (!link) return url;
    return `[${link.displayText}](${link.url})`;
  });

  return result;
}

/** Style rendered anchors like news cards: bracket label, new tab, secondary link. */
export function enhanceRenderedContentLinks(html) {
  if (!html) return "";

  if (typeof document !== "undefined") {
    const div = document.createElement("div");
    div.innerHTML = html;
    div.querySelectorAll("a").forEach((anchor) => {
      const href = anchor.getAttribute("href");
      if (!href) return;
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
      anchor.className = CONTENT_LINK_CLASS;
      anchor.textContent = formatAnchorLabel(anchor.textContent, href);
    });
    return div.innerHTML;
  }

  return html.replace(
    /<a\b([^>]*?)href="([^"]*)"([^>]*)>([\s\S]*?)<\/a>/gi,
    (_, _pre, href, _post, inner) => {
      const label = formatAnchorLabel(inner, href);
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="${CONTENT_LINK_CLASS}">${label}</a>`;
    },
  );
}
